import type { TextFacet } from "../structs.ts";
import type { CompiledMarkdown } from "./markdown.ts";
import { extractMentions } from "./rich-facets.ts";

export type Mark =
  | "delimiter"
  | "destination"
  | "bold"
  | "italic"
  | "link"
  | "mention"
  | "unresolved"
  | "tag";

export interface Span {
  readonly text: string;
  readonly marks: readonly Mark[];
}

interface Marked {
  readonly start: number;
  readonly end: number;
  readonly mark: Mark;
}

const facetMarks: Record<TextFacet["$type"], Mark> = {
  "blue.cerulea.app.facet#bold": "bold",
  "blue.cerulea.app.facet#italic": "italic",
  "blue.cerulea.app.facet#link": "link",
  "blue.cerulea.app.facet#mention": "mention",
  "blue.cerulea.app.facet#tag": "tag",
};

/**
 * Split composer source into runs marked the way it will publish. `facets` are the enriched
 * facets that would be sent, so the highlight cannot disagree with the post; handles that look
 * like mentions but did not resolve are marked separately.
 */
export function highlight(
  source: string,
  compiled: CompiledMarkdown,
  facets: readonly TextFacet[],
): Span[] {
  const toSource = (start: number, end: number) => ({
    start: compiled.sources[start]!,
    end: compiled.sources[end - 1]! + 1,
  });
  const ranges: Marked[] = [
    ...compiled.tokens.map((token) => ({
      start: token.start,
      end: token.end,
      mark: token.kind === "link-destination" ? "destination" as const : "delimiter" as const,
    })),
    ...facets.filter((facet) => facet.start < facet.end).map((facet) => ({
      ...toSource(facet.start, facet.end),
      mark: facetMarks[facet.$type],
    })),
    ...extractMentions(compiled.text, facets).map((mention) => ({
      ...toSource(mention.start, mention.end),
      mark: "unresolved" as const,
    })),
  ];

  ranges.sort((a, b) => a.start - b.start);
  const boundaries = [
    ...new Set([0, source.length, ...ranges.flatMap((range) => [range.start, range.end])]),
  ].sort((a, b) => a - b);
  const spans: Span[] = [];
  let open: Marked[] = [];
  let next = 0;
  for (const [index, start] of boundaries.entries()) {
    const end = boundaries[index + 1];
    if (end === undefined) break;
    open = open.filter((range) => range.end > start);
    while (next < ranges.length && ranges[next]!.start === start) open.push(ranges[next++]!);
    const marks = [...new Set(open.map((range) => range.mark))];
    spans.push({ text: source.slice(start, end), marks });
  }
  return spans;
}
